import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { Group, Menu } from '@mantine/core';

import { Skeleton } from '../../../../Feedback/Skeleton/Skeleton';
import {
  ActionMenuContainerProps,
  ActionMenuContext,
  ActionMenuDefaultTarget,
  ActionMenuItemProps,
} from './actions.types';
import { ActionMenuTarget } from './components/targets/ActionMenuTarget';
import { ActionMenuProvider, useActionMenuContext } from './context';

interface ActionMenuProps
  extends Omit<ActionMenuContext, 'setIsInitialLoading'>,
    PropsWithChildren {}

const ActionMenuItem = ({ children, visible = true, ...props }: ActionMenuItemProps) => {
  const { isLoading } = useActionMenuContext();
  if (!visible) return null;
  return (
    <Skeleton visible={isLoading}>
      <Menu.Item {...props}>{children}</Menu.Item>
    </Skeleton>
  );
};

const ActionMenuDropDownContent = ({ children }: PropsWithChildren) => {
  const { isInitialLoading } = useActionMenuContext();

  if (isInitialLoading) {
    return (
      <Group wrap="nowrap" p="sm">
        <Skeleton height={15} width={'8%'} />
        <Skeleton height={15} width={'70%'} />
      </Group>
    );
  }
  return <>{children}</>;
};

const ActionsMenuContainer = <TDefaultTarget extends ActionMenuDefaultTarget = 'button'>({
  Actions,
  Target,
  dropdownProps,
  menuProps,
  defaultTarget = 'button' as TDefaultTarget,
  defaultTargetProps,
}: ActionMenuContainerProps<TDefaultTarget>) => {
  const ctx = useActionMenuContext();

  return (
    <Menu
      shadow="md"
      position="bottom-end"
      transitionProps={{ transition: 'scale-y', duration: 150 }}
      {...menuProps}
      data-testid="action-menu-dropdown"
      keepMounted={Boolean(ctx.isKeepMounted)}
    >
      <Menu.Target>
        <ActionMenuTarget
          Target={Target}
          defaultTarget={defaultTarget}
          defaultTargetProps={defaultTargetProps}
        />
      </Menu.Target>
      <Menu.Dropdown miw={220} {...dropdownProps}>
        <Actions {...ctx} />
      </Menu.Dropdown>
    </Menu>
  );
};

const useLoadingDependencies = (dependencies: Array<boolean>) => {
  const { setIsInitialLoading } = useActionMenuContext();

  useEffect(() => {
    if (!dependencies.length) return;
    const hasLoading = dependencies.some((loading) => loading);

    if (hasLoading == false) {
      setIsInitialLoading?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are used to trigger effect
  }, [dependencies]);
};

const ActionMenu = <TDefaultTarget extends ActionMenuDefaultTarget = 'button'>({
  Actions,
  Target,
  defaultTarget = 'button' as TDefaultTarget,
  defaultTargetProps,
  ...rest
}: Partial<ActionMenuContainerProps<TDefaultTarget>>) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isKeepMounted, setIsKeepMounted] = useState(false);

  const handleSetIsKeepMounted = useCallback((keepMounted: boolean) => {
    // flushSync(() => {
    setIsKeepMounted(keepMounted);
    // });
  }, []);

  const contextValue: ActionMenuContext = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      isKeepMounted,
      setIsKeepMounted: handleSetIsKeepMounted,
      isInitialLoading,
      setIsInitialLoading,
      useLoadingDependencies,
    }),
    [isLoading, isKeepMounted, handleSetIsKeepMounted, isInitialLoading]
  );

  if (!Actions) {
    return null;
  }
  return (
    <ActionMenuProvider value={contextValue}>
      <ActionsMenuContainer
        Actions={Actions}
        Target={Target}
        defaultTarget={defaultTarget}
        defaultTargetProps={defaultTargetProps}
        {...rest}
      />
    </ActionMenuProvider>
  );
};

ActionMenu.Item = ActionMenuItem;
ActionMenu.Menu = ActionMenuDropDownContent;
ActionMenu.useContext = useActionMenuContext;

export { ActionMenu };
export type { ActionMenuContext, ActionMenuProps };
